/* AIEmailDraftModal — FÁZE 2: Voice/text command → AI generates email
 * for a single client → preview → send.
 *
 * Flow:
 *  1. User types or speaks: "Pošli Pavlovi info o servisní schůzce"
 *  2. POST /api/ai-email/draft → { subject, body_html, body_text, recipient_email }
 *  3. Preview HTML in iframe + editable subject + plaintext fallback
 *  4. Odeslání přes emailTransport — Gmail, nebo firemní e-mail (SMTP)
 */
import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { X, Sparkles, Mic, Send, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import ClientAutocomplete from "@/components/crm/ClientAutocomplete";
import DealAutocomplete from "@/components/crm/DealAutocomplete";
import { useLanguage } from "@/contexts/LanguageContext";
import { odesliEmail, zpravaChyby } from "@/lib/emailTransport";

import { TL } from "@/lib/prekladyUI";
/* V prohlížeči relativně (/api → proxy / ingress), stejně jako lib/api.js. Absolutní
 * REACT_APP_BACKEND_URL tu dřív dělal cross-origin volání: lokálně CORS blok
 * (přílohy u kandidáta v Náboru), na mobilu riziko neposlaných cookies. 24. 10. 2026 */
const API = typeof window !== "undefined" ? "" : (process.env.REACT_APP_BACKEND_URL || "");

export default function AIEmailDraftModal({ onClose, onSent, contactId = null, contactName = "", replyContext = null }) {
  const { t } = useLanguage();
  const initialCmd = replyContext
    ? `${t("auto.aIEmailDraftModal.odpovezNaEmailOd", "Odpověz na email od")} ${replyContext.to || t("auto.aIEmailDraftModal.klienta", "klienta")} ` +
      (replyContext.subject ? `${t("auto.aIEmailDraftModal.sPredmetem", "s předmětem")} '${replyContext.subject}'` : "") + ": "
    : "";
  const [command, setCommand] = useState(initialCmd);
  const [recording, setRecording] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  // QA-D2 — explicit klient + obchodní případ picker (předtím šlo jen přes „Pošli Pavlovi..." text příkaz)
  const [pickedContactId, setPickedContactId] = useState(contactId || "");
  const [pickedContactObj, setPickedContactObj] = useState(null);
  const [pickedDealId, setPickedDealId] = useState("");
  const [pickedDealObj, setPickedDealObj] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Jméno je uprostřed věty, proto předek + jméno + zádek. Zlomek lepený
     za jméno by v ukrajinštině a vietnamštině převrátil slovosled. */
  const placeholder = contactName
    ? `${t("auto.aIEmailDraftModal.posliPredJmenem", "Pošli")} ${contactName} ${t("auto.aIEmailDraftModal.emailOhledne", "email ohledně...")}`
    : (t("auto.aIEmailDraftModal.posliPavloviInfoO"));

  // ─── Voice recording ───
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeAudio(blob);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(t("auto.spolecne.mikrofonNeniDostupny"));
    }
  };

  const stopRec = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("audio", blob, "command.webm");
      // Quick transcribe via voice-notes endpoint, then place into command field
      const r = await fetch(`${API}/api/voice-notes/transcribe-only`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!r.ok) {
        // Fallback: use extract-client-fields just for the transcript
        toast.error(t("auto.spolecne.prepisSeNezdarilNapis"));
        return;
      }
      const data = await r.json();
      const txt = (data.transcript || "").trim();
      if (txt) {
        setCommand(txt);
        toast.success(t("auto.aIEmailDraftModal.prepsanoKlikniAiVygeneruj"));
      }
    } catch (e) {
      toast.error(t("auto.spolecne.chybaPrepisu"));
    }
  };

  // ─── Generate ───
  const handleGenerate = async () => {
    if (!command.trim()) {
      toast.error(t("auto.spolecne.napisNeboNahrajPrikaz"));
      return;
    }
    setGenerating(true);
    try {
      const r = await fetch(`${API}/api/ai-email/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ command: command.trim(), contact_id: pickedContactId || contactId, deal_id: pickedDealId || null }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || (t("auto.spolecne.aiGeneraceSelhala")));
        return;
      }
      const data = await r.json();
      setDraft(data);
    } catch (e) {
      toast.error(t("auto.spolecne.chybaSite"));
    } finally {
      setGenerating(false);
    }
  };

  // ─── Send ───
  const handleSend = async () => {
    if (!draft) return;
    setSending(true);
    try {
      // Odesílá se Gmailem NEBO firemním e-mailem (SMTP) — podle toho, co má
      // poradce připojené. Dřív to mířilo natvrdo na /api/gmail/send.
      await odesliEmail({
        to: [draft.recipient_email],
        subject: draft.subject,
        bodyHtml: draft.body_html,
        bodyText: draft.body_text,
        contactId: draft.contact_id,
      });
      toast.success(`${t("auto.aIEmailDraftModal.emailOdeslanSipka", "Email odeslán")} → ${draft.recipient_name}`);
      onSent?.();
    } catch (e) {
      toast.error(zpravaChyby(e, t("auto.spolecne.odeslaniSelhalo")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="rounded-xl overflow-hidden w-full max-w-3xl flex flex-col"
        style={{ background: "var(--surface)", maxHeight: "90vh", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
        data-testid="ai-draft-modal"
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-3"
          style={{ background: "linear-gradient(180deg, #C25934, #A84A29)", color: "#FFFFFF" }}
        >
          <Sparkles size={18} />
          <div className="flex-1">
            <h2 className="text-[14px] font-bold uppercase tracking-wide">{t("auto.aIEmailDraftModal.aiEmailKlientovi")}</h2>
          </div>
          <button aria-label={TL("Zavřít")} onClick={onClose} className="p-1 rounded hover:bg-white/20" data-testid="ai-draft-close">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* QA-D2 — explicit klient + obchod picker pro AI email */}
          <div className="mb-3 rounded-md p-2.5" style={{ background: "var(--accent-soft)", border: "1px dashed var(--accent)" }}>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: "var(--accent)" }}>{t("auto.aIEmailDraftModal.komuKlientACeho")}</div>
            <div className="grid grid-cols-2 gap-2">
              <ClientAutocomplete
                value={pickedContactId}
                onChange={(id, c) => { setPickedContactId(id); setPickedContactObj(c); }}
                testid="ai-draft-client"
                placeholder="Vyber klienta…"
              />
              <DealAutocomplete
                contactId={pickedContactId}
                value={pickedDealId}
                onChange={(id, d) => { setPickedDealId(id); setPickedDealObj(d); }}
                testid="ai-draft-deal"
                placeholder={t("auto.aIEmailDraftModal.volitelneObchod")}
              />
            </div>
            {pickedContactObj && (
              <div className="mt-1.5 text-[10.5px]" style={{ color: "var(--muted)" }}>
                {t("auto.aIEmailDraftModal.odeslatNa")}: <b style={{ color: "var(--ink)" }}>{pickedContactObj.email || t("auto.aIEmailDraftModal.klientNemaEmailVCrm", "(klient nemá e-mail v CRM)")}</b>
                {pickedDealObj && <span> · {t("auto.aIEmailDraftModal.obchod", "obchod")}: <b style={{ color: "var(--ink)" }}>{pickedDealObj.subject || pickedDealObj.title}</b></span>}
              </div>
            )}
          </div>

          {/* Step 1 — command */}
          <div className="mb-4">
            <label className="text-[11px] uppercase tracking-wide font-bold block mb-2" style={{ color: "var(--ink-muted)" }}>
              {t("auto.spolecne.n1HlasovyNeboTextovy")}
            </label>
            <div className="flex gap-2">
              <textarea
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                rows={2}
                placeholder={placeholder}
                className="flex-1 px-3 py-2 rounded-md text-[13px] outline-none resize-none"
                style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--ink)" }}
                data-testid="ai-draft-command"
              />
              <button
                onClick={recording ? stopRec : startRec}
                className="px-3 py-2 rounded-md flex items-center justify-center transition-all"
                style={{
                  background: recording ? "#DC2626" : "var(--accent-tint)",
                  color: recording ? "#FFFFFF" : "var(--accent)",
                  border: `1px solid ${recording ? "#DC2626" : "var(--accent)"}`,
                  minWidth: 44,
                }}
                title={recording ? (t("auto.aIEmailDraftModal.zastavitNahravani")) : (t("auto.aIEmailDraftModal.spustitNahravani"))}
                data-testid="ai-draft-mic-btn"
              >
                {recording ? (
                  <span className="text-[11px] font-bold animate-pulse">REC</span>
                ) : (
                  <Mic size={16} />
                )}
              </button>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating || !command.trim()}
              className="mt-3 w-full py-2 rounded-md font-semibold text-[13px] flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              style={{ background: "var(--accent)", color: "#FFFFFF" }}
              data-testid="ai-draft-generate-btn"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {generating ? t("auto.aIEmailDraftModal.aiPise", "AI píše…") : (t("auto.aIEmailDraftModal.aiVygenerujEmail"))}
            </button>
          </div>

          {/* Step 2 — preview */}
          {draft && (
            <div data-testid="ai-draft-preview">
              <label className="text-[11px] uppercase tracking-wide font-bold block mb-2" style={{ color: "var(--ink-muted)" }}>
                {t("auto.aIEmailDraftModal.n2NahledEmailu")}
              </label>
              <div className="rounded-md p-3 mb-3" style={{ background: "#FFFBF5", border: "1px solid #E8D5BC" }}>
                <div className="text-[11px] mb-2" style={{ color: "var(--ink-muted)" }}>
                  <strong>{t("auto.aIEmailDraftModal.komu", "Komu:")}</strong> {draft.recipient_name} &lt;{draft.recipient_email}&gt;
                </div>
                <div className="text-[11px] mb-2" style={{ color: "var(--ink-muted)" }}>
                  <strong>{t("auto.spolecne.predmet")}</strong>
                </div>
                <input
                  type="text"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="w-full px-2 py-1 mb-3 text-[13px] font-semibold rounded outline-none"
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--ink)" }}
                  data-testid="ai-draft-subject-input"
                />
                <div className="text-[11px] mb-1.5" style={{ color: "var(--ink-muted)" }}>
                  <strong>{t("auto.aIEmailDraftModal.telo")}</strong>
                </div>
                <div
                  className="rounded p-3 text-[13px] leading-relaxed"
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", maxHeight: 280, overflowY: "auto", color: "var(--ink)" }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(draft.body_html || (draft.body_text || "").replace(/\n/g, "<br>")) }}
                />
                {draft.explanation && (
                  <div className="mt-2 text-[10.5px] italic" style={{ color: "var(--ink-muted)" }}>
                    <Sparkles size={10} className="inline mr-1" />
                    {draft.explanation}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setDraft(null)}
                  className="px-3 py-2 rounded-md text-[12.5px] font-medium"
                  style={{ background: "var(--surface)", color: "var(--ink-muted)", border: "1px solid var(--border)" }}
                  data-testid="ai-draft-regenerate-btn"
                >
                  <Pencil size={12} className="inline mr-1" /> {t("auto.spolecne.zmenitPrikaz")}
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="flex-1 px-3 py-2 rounded-md font-semibold text-[13px] flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: "#16A34A", color: "#FFFFFF" }}
                  data-testid="ai-draft-send-btn"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                  {sending ? (t("auto.spolecne.odesilam")) : (t("auto.aIEmailDraftModal.odeslatZMehoGmailu"))}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
